import { useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useOrganization } from '@/contexts/OrganizationContext'
import { useAuth } from '@/contexts/AuthContext'
import type { ProjectionFormData } from '@/types/projections'
import type { Category, ProjectionWithRelations, ProjectionFilters } from '@/lib/supabase'
import { PROJECTION_KEYS, keepPreviousForOrg } from '@/lib/query-keys'
import { invalidateAfterProjectionWrite } from '@/lib/projection-invalidation'

// Definition moved to `@/lib/query-keys` so `import-configs.tsx` can read the
// keys without loading this module. Re-exported for the existing import sites;
// new code should import from `@/lib/query-keys` directly.
export { PROJECTION_KEYS }

/**
 * Query keys for categories data
 * All keys include organizationId to ensure proper cache isolation
 */
const CATEGORY_KEYS = {
  all: ['categories'] as const,
  lists: () => [...CATEGORY_KEYS.all, 'list'] as const,
  list: (organizationId: string | undefined) => [...CATEGORY_KEYS.lists(), organizationId] as const,
}

/**
 * Hook to fetch projections with optional filters and pagination.
 * Uses tenant-specific database client — no organizationId filtering in queries.
 * @param filters - Optional filters for date range, facility, category, type, and pagination
 * @returns Query result with paginated data { items: ProjectionWithRelations[], total: number }
 */
export function useProjections(filters?: ProjectionFilters) {
  const { currentOrganization } = useOrganization()

  return useQuery<{ items: ProjectionWithRelations[]; total: number }>({
    queryKey: PROJECTION_KEYS.list(currentOrganization?.id, filters),
    queryFn: async () => {
      // Import createTenantDb here to avoid circular dependency
      const { createTenantDb } = await import('@/lib/db-tenant')

      if (!currentOrganization?.id) {
        throw new Error('No organization selected')
      }

      const tenantDb = createTenantDb(currentOrganization.id)
      const { data, error } = await tenantDb.projections.list(filters)
      if (error) throw new Error(error.message)
      return data as unknown as { items: ProjectionWithRelations[]; total: number }
    },
    enabled: !!currentOrganization?.id,
    // The total travels with each page; keep it while the next page loads.
    placeholderData: keepPreviousForOrg(currentOrganization?.id, 2),
  })
}

/**
 * DCP-406: hook returning a lookup of the Projections table page that shows
 * the first (in table order) of a set of rows — used after a create to open
 * the page where the new rows landed, since the date sort rarely puts them on
 * page 1.
 *
 * An imperative one-off, not a query: it answers "where is this row NOW" at
 * the moment of the create, and caching that answer would be wrong as soon as
 * any row is added or removed.
 *
 * @returns a stable callback `(ids, filters, pageSize)` resolving to the
 *   1-based page, or `null` when none of `ids` is visible under `filters`.
 *   Rejects when no organization is selected or a query failed.
 */
export function useFindProjectionsPage() {
  const { currentOrganization } = useOrganization()
  const organizationId = currentOrganization?.id

  return useCallback(
    async (ids: string[], filters: ProjectionFilters | undefined, pageSize: number): Promise<number | null> => {
      // Import createTenantDb here to avoid circular dependency
      const { createTenantDb } = await import('@/lib/db-tenant')

      if (!organizationId) {
        throw new Error('No organization selected')
      }

      const tenantDb = createTenantDb(organizationId)
      const { data, error } = await tenantDb.projections.findPage(ids, filters, pageSize)
      if (error) throw new Error(error.message)
      return data
    },
    [organizationId],
  )
}

/**
 * Hook to fetch categories for the current organization.
 * Uses tenant-specific database client.
 * @returns Query result with Category[] data
 */
export function useCategories() {
  const { currentOrganization } = useOrganization()

  return useQuery<Category[]>({
    queryKey: CATEGORY_KEYS.list(currentOrganization?.id),
    queryFn: async () => {
      // Import createTenantDb here to avoid circular dependency
      const { createTenantDb } = await import('@/lib/db-tenant')

      if (!currentOrganization?.id) {
        throw new Error('No organization selected')
      }

      const tenantDb = createTenantDb(currentOrganization.id)
      const { data, error } = await tenantDb.categories.list({ pageSize: 0 })
      if (error) throw new Error(error.message)
      return data.items
    },
    enabled: !!currentOrganization?.id,
  })
}

/**
 * Hook to create a new projection.
 * Creates a projection in the tenant schema — no organization_id needed.
 * @returns Mutation hook that creates a projection and invalidates related queries
 */
export function useCreateProjection() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useOrganization()
  const { user } = useAuth()

  return useMutation({
    mutationFn: async (params: ProjectionFormData) => {
      // Import createTenantDb here to avoid circular dependency
      const { createTenantDb } = await import('@/lib/db-tenant')

      if (!currentOrganization?.id) {
        throw new Error('No organization selected')
      }

      if (!user?.id) {
        throw new Error('User not authenticated')
      }

      const tenantDb = createTenantDb(currentOrganization.id)
      const { data, error } = await tenantDb.projections.create({
        ...params,
        created_by: user.id,
      })

      if (error) throw new Error(error.message)
      return data
    },
    onSuccess: () => {
      invalidateAfterProjectionWrite(queryClient)
    },
  })
}

/**
 * Hook to create multiple projections in a single database call.
 * Creates projections in the tenant schema — no organization_id needed.
 * @returns Mutation hook that creates projections and invalidates related queries
 */
export function useCreateProjections() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useOrganization()
  const { user } = useAuth()

  return useMutation({
    mutationFn: async (params: ProjectionFormData[]) => {
      const { createTenantDb } = await import('@/lib/db-tenant')

      if (!currentOrganization?.id) {
        throw new Error('No organization selected')
      }

      if (!user?.id) {
        throw new Error('User not authenticated')
      }

      const tenantDb = createTenantDb(currentOrganization.id)
      const { data, error } = await tenantDb.projections.createMany(
        params.map((p) => ({
          ...p,
          created_by: user.id,
        }))
      )

      if (error) throw new Error(error.message)
      return data
    },
    onSuccess: () => {
      invalidateAfterProjectionWrite(queryClient)
    },
  })
}

/**
 * Hook to update an existing projection.
 * Updates a projection in the tenant schema.
 * @returns Mutation hook that updates a projection and invalidates related queries
 */
export function useUpdateProjection() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useOrganization()

  return useMutation({
    mutationFn: async (params: { id: string; data: Partial<ProjectionFormData> }) => {
      // Import createTenantDb here to avoid circular dependency
      const { createTenantDb } = await import('@/lib/db-tenant')

      if (!currentOrganization?.id) {
        throw new Error('No organization selected')
      }

      const tenantDb = createTenantDb(currentOrganization.id)
      const { data, error } = await tenantDb.projections.update(params.id, params.data)
      if (error) throw new Error(error.message)
      return data
    },
    onSuccess: () => {
      invalidateAfterProjectionWrite(queryClient)
    },
  })
}

/**
 * Hook to delete a projection.
 * Deletes a projection from the tenant schema.
 * @returns Mutation hook that deletes a projection and invalidates related queries
 */
export function useDeleteProjection() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useOrganization()

  return useMutation({
    mutationFn: async (id: string) => {
      // Import createTenantDb here to avoid circular dependency
      const { createTenantDb } = await import('@/lib/db-tenant')

      if (!currentOrganization?.id) {
        throw new Error('No organization selected')
      }

      const tenantDb = createTenantDb(currentOrganization.id)
      const { error } = await tenantDb.projections.delete(id)
      if (error) throw new Error(error.message)
    },
    onSuccess: () => {
      invalidateAfterProjectionWrite(queryClient)
    },
  })
}
